import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import { api } from "../api/client";
import { getThemeStyles, DEFAULT_THEME } from "../themeConfig";

export default function CreatePage() {
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [generatedHtml, setGeneratedHtml] = useState("");
  const [currentSparkId, setCurrentSparkId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const { user, isAuthenticated, updateUserPoints, logout } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const themeKey = user?.theme ?? DEFAULT_THEME;
  const t = getThemeStyles(themeKey);
  const hasPoints = (user?.points ?? 0) > 0;

  // 如果未登录，重定向到登录页
  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
    }
  }, [isAuthenticated, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const placeholderHtml = "<h2 style='text-align:center; margin-top:50%; font-family:sans-serif; color:#666;'>✨ 魔法生成中...</h2>";
  const canSave = Boolean(generatedHtml && generatedHtml !== placeholderHtml);

  const generateApp = async () => {
    if (!hasPoints) {
      showToast("💎不够，无法施展新的魔法，请先补充💎～", "warning");
      return;
    }

    if (!prompt.trim()) {
      showToast("请先许个愿吧！", "warning");
      return;
    }

    setLoading(true);
    setCurrentSparkId(null);
    setGeneratedHtml(placeholderHtml);

    try {
      const data = await api.createMagic(prompt);
      setGeneratedHtml(data.html);
      showToast("✅ 魔法生成完成！满意的话记得点击下方保存～", "success");
    } catch (err) {
      setGeneratedHtml(`
        <div style="text-align:center; margin-top:50%; font-family:sans-serif; color:#666; padding:20px;">
          <h2>哎呀，魔法连接中断了！</h2>
          <p>请检查后端是否启动，或稍后再试。</p>
        </div>
      `);
      const message = err instanceof Error ? err.message : "生成失败，请稍后再试";
      showToast(message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSpark = async () => {
    const trimmedPrompt = prompt.trim();
    if (!trimmedPrompt || !canSave) {
      showToast("请先生成一个满意的魔法再保存～", "warning");
      return;
    }

    if (!hasPoints) {
      showToast("💎不够，无法保存新的魔法～", "warning");
      return;
    }

    setSaving(true);
    try {
      const response = await api.saveSpark({
        name: trimmedPrompt.slice(0, 60),
        prompt: trimmedPrompt,
        html: generatedHtml,
        is_public: false,
      });

      setCurrentSparkId(response.spark.id);
      updateUserPoints(response.remaining_points);
      showToast("✨ 魔法已保存到作品库，快去看看吧！", "success");
    } catch (error) {
      const message = error instanceof Error ? error.message : "保存失败，请稍后再试";
      showToast(message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`min-h-screen ${t.bg} text-white p-6 font-mono transition-all duration-300`}>
      <div className="flex justify-between items-center mb-6 gap-3 flex-wrap">
        <button
          onClick={() => navigate("/")}
          className={`px-4 py-2 rounded-xl border-2 font-bold ${t.button1} hover:scale-105 transition`}
        >
          ← 返回首页
        </button>
        <div className="flex gap-3">
          <button
            onClick={() => navigate("/profile")}
            className={`px-4 py-2 rounded-xl border-2 font-bold ${t.button1} hover:scale-105 transition`}
          >
            个人中心
          </button>
          <button
            onClick={handleLogout}
            className={`px-4 py-2 rounded-xl border-2 font-bold ${t.button1} hover:scale-105 transition`}
          >
            退出登录
          </button>
        </div>
      </div>

      {/* HEADER */}
      <header className="text-center mb-6">
        <h1 className={`text-4xl font-bold tracking-wide drop-shadow-lg ${t.header}`}>
          🎨 魔法 App 生成器
        </h1>
        {user && (
          <p className={`text-sm mt-2 ${t.subheader}`}>
            欢迎，{user.user_name}！💎：{user.points}
          </p>
        )}
      </header>

      <div className="max-w-6xl mx-auto">
        {/* INPUT SECTION */}
        <div className={`p-6 rounded-3xl shadow-xl border-4 ${t.card} mb-6`}>
          <h2 className={`text-2xl font-bold mb-4 ${t.subheader}`}>
            ✨ 说出你的愿望...
          </h2>
          <div className="flex gap-4">
            <input
              type="text"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && !loading && generateApp()}
              className={`flex-1 px-4 py-3 rounded-xl border-2 ${t.input} focus:outline-none focus:ring-2 focus:ring-opacity-50`}
              placeholder="例如：做一个打外星人的游戏，或者能够倒计时的番茄钟"
              disabled={loading}
            />
            <button
              onClick={generateApp}
              disabled={loading || !hasPoints}
              className={`px-8 py-3 rounded-xl border-2 ${t.button1} text-white font-bold text-lg hover:scale-105 transition disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {loading ? "🔮 正在施法中..." : "✨ 施展魔法！"}
            </button>
          </div>
          {!hasPoints && user && (
            <p className="text-sm text-red-200 mt-2">
              当前💎不够，无法施展新的魔法，请先补充💎。
            </p>
          )}
        </div>

        {/* PHONE MOCKUP */}
        <div className={`p-4 rounded-3xl shadow-xl border-4 ${t.card}`}>
          <div className="w-full max-w-sm mx-auto">
            <div className="bg-black rounded-[30px] p-2 shadow-2xl">
              <div className="bg-white rounded-[20px] overflow-hidden" style={{ aspectRatio: "9/16" }}>
                {generatedHtml ? (
                  <iframe
                    srcDoc={generatedHtml}
                    className="w-full h-full border-none"
                    title="Generated App"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-100">
                    <p className={`text-gray-400 ${t.subheader}`}>
                      输入你的想法，然后点击"施展魔法！"
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="mt-4 flex flex-col items-center gap-3">
            <button
              onClick={handleSaveSpark}
              disabled={!canSave || saving || loading || !hasPoints}
              className={`px-8 py-3 rounded-xl border-2 ${t.button1} text-white font-bold text-lg hover:scale-105 transition disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {saving ? "💾 保存中..." : "💾 保存这个魔法"}
            </button>
            {currentSparkId && (
              <button
                onClick={() => navigate("/my-sparks")}
                className={`px-8 py-3 rounded-xl border-2 ${t.button1} text-white font-bold text-lg hover:scale-105 transition`}
              >
                📚 查看我的作品
              </button>
            )}
          </div>
        </div>

        {/* BACK BUTTON */}
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate("/")}
            className={`px-6 py-3 rounded-xl border-2 ${t.button1} text-white font-bold hover:scale-105 transition`}
          >
            ← 返回首页
          </button>
        </div>
      </div>
    </div>
  );
}

